// Badge API types, as served by n450s_backend under /api/badges (badges-3).
// Keep in step with _future/badges-3/BRIEF.md "HTTP API", and for the goals and
// nudges with _future/badges-4/BRIEF.md "HTTP API additions".

export type Tier = "PAPER" | "ROCK" | "BRONZE" | "SILVER" | "GOLD" | "RUBY" | "EMERALD" | "DIAMOND";
export type RarityClass = "common" | "uncommon" | "rare" | "epic" | "legendary";
export type RevealLevel = "tile" | "card" | "cinematic" | "backfill";

export const TIERS: readonly Tier[] = ["PAPER", "ROCK", "BRONZE", "SILVER", "GOLD", "RUBY", "EMERALD", "DIAMOND"];
export const RARITY_CLASSES: readonly RarityClass[] = ["common", "uncommon", "rare", "epic", "legendary"];

export interface BadgeRarity {
  class: RarityClass;
  holders: number;
  members: number;
  pct: number;
}

export interface RarityAtEarn {
  class: RarityClass;
  holders: number;
  members: number;
}

/** A catalogue entry as the SPA sees it. */
export interface BadgeDef {
  code: string;
  name: string;
  description: string;
  category: string;
  family: string;
  tier: Tier;
  /** What the art shows; null for badges without a number (CARD_LEGENDARY). */
  number: number | null;
  threshold: number;
  /** The base value: what the first copy is worth. Repeats are worth less (see value.ts). */
  points: number;
  repeatable: boolean;
  unique: boolean;
  rarity: BadgeRarity;
}

export interface BadgeCopy {
  copyId: string;
  dateEarned: string;
  awardedAt: string;
  collected: boolean;
  backfill: boolean;
  repeatIndex: number;
  firstEver: boolean;
  rarityAtEarn: RarityAtEarn | null;
  /**
   * What this copy is worth: points * 0.955^(repeatIndex-1), two decimals.
   * Missing from an older backend; read it through valueOf() in value.ts.
   */
  value?: number;
}

export interface HeldBadge extends BadgeDef {
  count: number;
  /**
   * What every held copy is worth together, two decimals. Missing from an older
   * backend; read it through totalValueOf() in value.ts.
   */
  totalValue?: number;
  firstEarnedAt: string;
  lastEarnedAt: string;
  pinned: boolean;
  /** Newest first. */
  copies: BadgeCopy[];
}

/** The next tier the member does not hold yet. */
export interface CategoryProgress {
  category: string;
  family: string;
  current: number;
  /** null when every tier is held. */
  next: BadgeDef | null;
  /** 0..100 toward next.threshold. */
  pct: number;
}

export interface UnrevealedCopy extends BadgeDef {
  copyId: string;
  dateEarned: string;
  repeatIndex: number;
  count: number;
  backfill: boolean;
  firstEver: boolean;
  revealLevel: RevealLevel;
  rarityAtEarn: RarityAtEarn | null;
  /**
   * What this copy is worth: points * 0.955^(repeatIndex-1), two decimals.
   * Missing from an older backend; read it through valueOf() in value.ts.
   */
  value?: number;
}

/**
 * The next badge within reach in one category (badges-4). The server words the
 * line and picks at most three, closest first.
 */
export interface BadgeGoal {
  category: string;
  family: string;
  kind: "count" | "streak";
  /** 23 coffees, or 5 race days in a row. */
  current: number;
  /** next.threshold */
  target: number;
  /** target - current, at least 1. */
  remaining: number;
  /** 0..100 */
  pct: number;
  /** "2 more coffees", "Race 2 more days in a row" */
  line: string;
  /** The badge it earns (same shape as /catalog items). */
  next: BadgeDef;
}

/** A goal an action just moved, shown once as a small toast (badges-4). */
export interface BadgeNudge extends BadgeGoal {
  /** Stable per member and category. */
  id: string;
  /** ISO, when the action moved it. */
  at: string;
}

// ---------- responses and request bodies ----------

/** GET /api/badges/me */
export interface MyBadgesResponse {
  badges: HeldBadge[];
  progress: CategoryProgress[];
  pinned: string[];
}

/** GET /api/badges/catalog */
export interface BadgeCatalogResponse {
  badges: BadgeDef[];
}

/** GET /api/badges/me/unrevealed */
export interface UnrevealedResponse {
  items: UnrevealedCopy[];
  /** Unseen nudges from the last 15 minutes (badges-4). Missing from an older backend. */
  nudges?: BadgeNudge[];
}

/** GET /api/badges/me/next */
export interface MyNextBadgesResponse {
  goals: BadgeGoal[];
}

/** POST /api/badges/me/nudges/seen */
export interface MarkBadgeNudgesSeenRequest {
  ids: string[];
}
export interface MarkBadgeNudgesSeenResponse {
  seen: number;
}

/** POST /api/badges/me/collect */
export interface CollectBadgesRequest {
  copyIds?: string[];
  all?: boolean;
  backfillOnly?: boolean;
}
export interface CollectBadgesResponse {
  collected: number;
}

/** PUT /api/badges/me/pins */
export interface SetBadgePinsRequest {
  codes: string[];
}
export interface SetBadgePinsResponse {
  pinned: string[];
}

/** GET /api/badges/me/rank. Private: only ever the caller's own numbers. */
export interface MyBadgeRankResponse {
  rank: number | null;
  of: number;
  percentile: number | null;
  score: number;
  pointsToNext: number | null;
  previousRank: number | null;
  distinct: number;
  copies: number;
}

/** POST /api/badges/me/share */
export interface ShareBadgeRequest {
  code: string;
}
export interface ShareBadgeResponse {
  shareId: string;
  /** "/badge/s/<shareId>" */
  path: string;
}

/** GET /api/badges/share/:shareId (public, no auth) */
export interface SharedBadgeResponse {
  username: string;
  badge: BadgeDef;
  count: number;
  firstEarnedAt: string;
  firstEver: boolean;
  rarityAtEarn: RarityAtEarn | null;
}

/** GET /api/badges/user/:username (members) */
export type PublicHeldBadge = Omit<HeldBadge, "copies">;
export interface UserBadgesResponse {
  username: string;
  pinned: string[];
  badges: PublicHeldBadge[];
}

/** GET /api/badges/recent?days=7 (members) */
export interface RecentBadgeItem {
  username: string;
  code: string;
  name: string;
  category: string;
  tier: Tier;
  number: number | null;
  rarity: RarityClass;
  count: number;
  repeatIndex: number;
  earnedAt: string;
}
export interface RecentBadgesResponse {
  items: RecentBadgeItem[];
}

/** POST /api/badges/admin/sweep (admin) */
export interface BadgeSweepRequest {
  dryRun?: boolean;
}
/**
 * Engine result summary. The backend owns the exact shape; the admin UI shows
 * the per-code counts and the totals, and keeps anything else as raw JSON.
 */
export interface BadgeSweepResponse {
  dryRun?: boolean;
  awarded?: number;
  users?: number;
  byCode?: Record<string, number>;
  [key: string]: unknown;
}
